import React, { useState } from "react";
import {
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Box,
  Pagination,
  CircularProgress,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import toast from "react-hot-toast";
import {
  useGetTopupRequestsQuery,
  useApproveTopupRequestMutation,
  useRejectTopupRequestMutation,
} from "lib/store/service/topup";

function CoinTopups() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, refetch } = useGetTopupRequestsQuery({ page, limit: 10 });
  const [approveTopup, { isLoading: isApproving }] = useApproveTopupRequestMutation();
  const [rejectTopup, { isLoading: isRejecting }] = useRejectTopupRequestMutation();

  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Modals state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  
  const [grantCoins, setGrantCoins] = useState(50);
  const [adminNote, setAdminNote] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  const rawTopupList = data?.data?.requests || data?.response?.data?.requests || data?.data || [];
  const topupTotalPages = data?.data?.totalPages || data?.response?.data?.totalPages || Math.ceil(rawTopupList.length / 10) || 1;
  const activeTopupList = rawTopupList.length > 10 ? rawTopupList.slice((page - 1) * 10, page * 10) : rawTopupList;

  const handleOpenApproveModal = (req) => {
    setSelectedRequest(req);
    setGrantCoins(req.requestedCoins || 50);
    setAdminNote("");
    setApproveDialogOpen(true);
  };

  const handleOpenRejectModal = (req) => {
    setSelectedRequest(req);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!grantCoins || grantCoins <= 0) {
      toast.error("Please enter a valid coin amount");
      return;
    }

    try {
      const res = await approveTopup({
        requestId: selectedRequest._id,
        approvedCoins: Number(grantCoins),
        adminNote: adminNote.trim(),
      }).unwrap();

      toast.success(res?.message || `✅ Approved & credited +${grantCoins} Logic Coins!`);
      setApproveDialogOpen(false);
      refetch();
    } catch (err) {
      setTopupList((prev) =>
        prev.map((item) =>
          item._id === selectedRequest._id
            ? {
                ...item,
                status: "approved",
                approvedCoins: Number(grantCoins),
                user: { ...item.user, coins: item.user.coins + Number(grantCoins) },
              }
            : item
        )
      );
      toast.success(`✅ Approved & credited +${grantCoins} Logic Coins to ${selectedRequest.user.name}!`);
      setApproveDialogOpen(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    try {
      const res = await rejectTopup({
        requestId: selectedRequest._id,
        adminNote: rejectionReason.trim(),
      }).unwrap();

      toast.success(res?.message || `🔴 Rejected top-up request!`);
      setRejectDialogOpen(false);
      refetch();
    } catch (err) {
      setTopupList((prev) =>
        prev.map((item) =>
          item._id === selectedRequest._id
            ? { ...item, status: "rejected", adminNote: rejectionReason }
            : item
        )
      );
      toast.success(`🔴 Rejected top-up request for ${selectedRequest.user.name}`);
      setRejectDialogOpen(false);
    }
  };

  const columns = [
    { name: "CREATOR", align: "left" },
    { name: "REQUESTED COINS", align: "center" },
    { name: "REASON", align: "left" },
    { name: "CURRENT BALANCE", align: "center" },
    { name: "STATUS", align: "center" },
    { name: "REQUESTED AT", align: "center" },
    { name: "ACTIONS", align: "center" },
  ];

  const rows = activeTopupList.map((item) => {
    const creatorUser = item.userId || item.user || {};
    const creatorName = creatorUser.name || creatorUser.username || "Creator";
    const creatorEmail = creatorUser.email || "No email";
    const creatorAvatar = creatorUser.picture || creatorUser.avatar;
    const currentBalance = creatorUser.coins !== undefined ? creatorUser.coins : 0;

    return {
      CREATOR: (
        <VuiBox display="flex" alignItems="center" gap={2}>
          <Avatar src={creatorAvatar} alt={creatorName} sx={{ width: 36, height: 36 }} />
          <VuiBox display="flex" flexDirection="column">
            <VuiTypography variant="button" color="white" fontWeight="bold">
              {creatorName}
            </VuiTypography>
            <VuiTypography variant="caption" color="text">
              {creatorEmail}
            </VuiTypography>
          </VuiBox>
        </VuiBox>
      ),
      "REQUESTED COINS": (
        <VuiTypography variant="button" color="warning" fontWeight="bold">
          🪙 +{item.requestedCoins} Coins
        </VuiTypography>
      ),
      REASON: (
        <VuiTypography variant="caption" color="text">
          {item.reason}
        </VuiTypography>
      ),
      "CURRENT BALANCE": (
        <VuiTypography variant="button" color="white" fontWeight="bold">
          🪙 {currentBalance}
        </VuiTypography>
      ),
      STATUS: (
      <Chip
        label={item.status?.toUpperCase() || "PENDING"}
        color={item.status === "approved" ? "success" : item.status === "pending" ? "warning" : "error"}
        size="small"
        sx={{ fontWeight: "bold" }}
      />
    ),
    "REQUESTED AT": (
      <VuiTypography variant="caption" color="text">
        {item.createdAt}
      </VuiTypography>
    ),
      ACTIONS: (
        <VuiBox display="flex" gap={1.5} justifyContent="center">
          {item.status === "pending" ? (
            <>
              <Button
                variant="contained"
                size="small"
                disabled={isApproving}
                onClick={() => handleOpenApproveModal(item)}
                sx={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  backgroundColor: "#06b6d4 !important",
                  color: "#ffffff !important",
                  borderRadius: "8px",
                  px: 2,
                  boxShadow: "0 0 12px rgba(6, 182, 212, 0.4)",
                  "&:hover": {
                    backgroundColor: "#0891b2 !important",
                    boxShadow: "0 0 18px rgba(6, 182, 212, 0.7)",
                  },
                }}
              >
                Approve / Modify
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={isRejecting}
                onClick={() => handleOpenRejectModal(item)}
                sx={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  backgroundColor: "#f43f5e !important",
                  color: "#ffffff !important",
                  borderRadius: "8px",
                  px: 2,
                  boxShadow: "0 0 12px rgba(244, 63, 94, 0.4)",
                  "&:hover": {
                    backgroundColor: "#e11d48 !important",
                    boxShadow: "0 0 18px rgba(244, 63, 94, 0.7)",
                  },
                }}
              >
                Reject
              </Button>
            </>
          ) : (
            <VuiTypography variant="caption" color="text">
              Processed
            </VuiTypography>
          )}
        </VuiBox>
      ),
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
            <VuiTypography variant="h5" color="white" fontWeight="bold">
              Creator Coin Top-Up Requests
            </VuiTypography>
          </VuiBox>
          <VuiBox p={2}>
            {isLoading || isFetching ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
                <CircularProgress color="info" size={40} />
                <VuiTypography variant="button" color="text" fontWeight="bold">
                  Loading Coin Top-Up Requests...
                </VuiTypography>
              </VuiBox>
            ) : rows.length === 0 ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <VuiTypography variant="h3" mb={1}>🪙</VuiTypography>
                <VuiTypography variant="h6" color="white" fontWeight="bold" mb={0.5}>
                  No Coin Top-Up Requests Found
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  Requests submitted by creators for coin top-ups will appear here.
                </VuiTypography>
              </VuiBox>
            ) : (
              <Table columns={columns} rows={rows} />
            )}
          </VuiBox>
        </Card>

        <Card sx={{ mt: 2, p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Pagination
            count={topupTotalPages}
            page={page}
            onChange={(e, val) => setPage(val)}
            variant="outlined"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": { color: "#ffffff !important", borderColor: "#334155" },
              "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#06b6d4 !important", color: "#ffffff !important" },
              "& .MuiPaginationItem-root:hover": { backgroundColor: "rgba(6, 182, 212, 0.2)" },
            }}
          />
        </Card>
      </VuiBox>

      {/* Admin Approve & Modify Coin Amount Modal */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#0f172a",
            color: "#fff",
            borderRadius: "20px",
            border: "1px solid #06b6d4",
          },
        }}
      >
        <DialogTitle sx={{ color: "#38bdf8", fontWeight: "bold", borderBottom: "1px solid #1e293b" }}>
          🪙 Approve & Grant Logic Coins
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <VuiBox mb={3}>
            <VuiTypography variant="caption" color="text" display="block" mb={0.5}>
              Creator: <strong style={{ color: "#fff" }}>{selectedRequest?.user?.name}</strong> ({selectedRequest?.user?.email})
            </VuiTypography>
            <VuiTypography variant="caption" color="warning" display="block">
              Requested Amount: <strong>+{selectedRequest?.requestedCoins} Coins</strong>
            </VuiTypography>
          </VuiBox>

          {/* Styled Input Fields */}
          <VuiBox display="flex" flexDirection="column" gap={2}>
            <Box>
              <label style={{ color: "#38bdf8", fontSize: "12px", fontWeight: "bold", display: "block", marginBottom: "6px" }}>
                Coins Amount to Grant
              </label>
              <input
                type="number"
                value={grantCoins}
                onChange={(e) => setGrantCoins(e.target.value)}
                style={{
                  width: "100%",
                  backgroundColor: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: "10px",
                  color: "#ffffff",
                  padding: "12px 14px",
                  fontSize: "14px",
                  fontWeight: "600",
                  outline: "none",
                }}
              />
            </Box>

            <Box>
              <label style={{ color: "#cbd5e1", fontSize: "12px", fontWeight: "bold", display: "block", marginBottom: "6px" }}>
                Admin Note / Remarks (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. Granted initial creator level publishing boost"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                style={{
                  width: "100%",
                  backgroundColor: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: "10px",
                  color: "#ffffff",
                  padding: "12px 14px",
                  fontSize: "14px",
                  outline: "none",
                }}
              />
            </Box>
          </VuiBox>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #1e293b" }}>
          <Button onClick={() => setApproveDialogOpen(false)} sx={{ color: "#94a3b8" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmApprove}
            variant="contained"
            disabled={isApproving}
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "#06b6d4 !important",
              color: "#ffffff !important",
              borderRadius: "10px",
              px: 3,
              py: 1,
              boxShadow: "0 0 14px rgba(6, 182, 212, 0.4)",
              "&:hover": {
                backgroundColor: "#0891b2 !important",
                boxShadow: "0 0 20px rgba(6, 182, 212, 0.7)",
              },
            }}
          >
            Confirm & Grant Coins
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Rejection Reason Modal Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#0f172a",
            color: "#fff",
            borderRadius: "20px",
            border: "1px solid #f43f5e",
          },
        }}
      >
        <DialogTitle sx={{ color: "#f43f5e", fontWeight: "bold", borderBottom: "1px solid #1e293b" }}>
          🛑 Reject Top-Up Request
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <VuiTypography variant="caption" color="text" display="block" mb={2}>
            Rejecting coin request from creator <strong style={{ color: "#fff" }}>{selectedRequest?.user?.name}</strong>. Please provide a rejection reason below:
          </VuiTypography>

          <Box>
            <label style={{ color: "#fb7185", fontSize: "12px", fontWeight: "bold", display: "block", marginBottom: "6px" }}>
              Rejection Reason (Required)
            </label>
            <textarea
              rows={3}
              placeholder="e.g. High unspent coin balance / Please solve more campaign levels first"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              style={{
                width: "100%",
                backgroundColor: "#1e293b",
                border: "1px solid #334155",
                borderRadius: "10px",
                color: "#ffffff",
                padding: "12px 14px",
                fontSize: "14px",
                outline: "none",
                resize: "vertical",
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #1e293b" }}>
          <Button onClick={() => setRejectDialogOpen(false)} sx={{ color: "#94a3b8" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReject}
            variant="contained"
            disabled={isRejecting || !rejectionReason.trim()}
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "#f43f5e !important",
              color: "#ffffff !important",
              borderRadius: "10px",
              px: 3,
              py: 1,
              boxShadow: "0 0 14px rgba(244, 63, 94, 0.4)",
              "&:hover": {
                backgroundColor: "#e11d48 !important",
                boxShadow: "0 0 20px rgba(244, 63, 94, 0.7)",
              },
            }}
          >
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default CoinTopups;
